// src/components/MedicinesPage/MedicationHistoryCardDoctor/MedicationHistoryCardDoctor.jsx
"use client";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import {
  fetchMedications,
  fetchArchivedMedications,
} from "../../redux/thunks/medicationThunks";
import {
  selectActiveMedications,
  selectArchivedMedications,
  selectMedicationState,
} from "../../redux/slices/medicationSlice";
import "./MedicationHistoryCard.css";

/* =========================
 * Configuration uploads
 * ========================= */
const UPLOADS_BASE =
  import.meta?.env?.VITE_MEDICATION_UPLOADS_URL || "http://localhost:3010/uploads";

/* =========================
 * Default medication image (SVG inline as data URI)
 * ========================= */
const DEFAULT_MEDICATION_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 88 88"><rect width="88" height="88" rx="16" fill="#DBEAFE"/><g transform="translate(44,44) rotate(-35)"><rect x="-18" y="-9" width="18" height="18" rx="9" fill="#DBEAFE"/><rect x="0" y="-9" width="18" height="18" rx="9" fill="#DBEAFE" opacity="0.5"/><rect x="-18" y="-9" width="36" height="18" rx="9" fill="none" stroke="#2B4F86" stroke-width="2.2"/><line x1="0" y1="-9" x2="0" y2="9" stroke="#2B4F86" stroke-width="2.2" stroke-linecap="round"/></g></svg>`;

const DEFAULT_MEDICATION_IMAGE = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(DEFAULT_MEDICATION_SVG)}`;

/* =========================
 * Valeurs à ignorer (pas d'image réelle)
 * ========================= */
const IGNORED_IMAGE_VALUES = new Set([
  "default",
  "default.png",
  "default.jpg",
  "default.jpeg",
  "placeholder",
  "placeholder.png",
  "null",
  "undefined",
  "none",
  "",
]);

/* =========================
 * Helper: vérifier si c'est une vraie image
 * ========================= */
const isValidImageValue = (img) => {
  if (!img) return false;
  const value = String(img).trim().toLowerCase();
  if (!value) return false;
  if (IGNORED_IMAGE_VALUES.has(value)) return false;
  return true;
};

/* =========================
 * Helper pour formater les dates
 * ========================= */
const formatDate = (dateString, locale = "en-GB") => {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return null;
  }
};

/* =========================
 * Helper pour calculer la durée
 * ========================= */
const calculateDurationKey = (startDate, endDate) => {
  if (!startDate) return { key: "unknown", params: {} };

  try {
    const start = new Date(startDate);
    if (Number.isNaN(start.getTime())) return { key: "unknown", params: {} };

    const end = endDate ? new Date(endDate) : new Date();
    if (Number.isNaN(end.getTime())) return { key: "unknown", params: {} };

    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return { key: "today", params: {} };
    if (diffDays === 1) return { key: "days", params: { count: 1 } };
    if (diffDays < 7) return { key: "days", params: { count: diffDays } };
    if (diffDays < 30) return { key: "weeks", params: { count: Math.floor(diffDays / 7) } };
    if (diffDays < 365) return { key: "months", params: { count: Math.floor(diffDays / 30) } };
    return { key: "years", params: { count: Math.floor(diffDays / 365) } };
  } catch {
    return { key: "unknown", params: {} };
  }
};

/* =========================
 * Déterminer le statut d'un médicament
 * ========================= */
const getMedicationStatus = (medication) => {
  if (medication.isArchived) return "completed";

  if (Array.isArray(medication.takenPerDose) && medication.takenPerDose.length > 0) {
    const allTaken = medication.takenPerDose.every((taken) => taken === true);
    if (allTaken) return "completed";
  }

  return "ongoing";
};

export default function MedicationHistoryCardDoctor({
  patientId,
  title,
  subtitle,
  placeholder,
  onSearchChange,
  disableFetch = true, // 🔒 Par défaut, ne fait pas d'appels backend (pour la vue docteur)
}) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const [query, setQuery] = useState("");
  const [failedImages, setFailedImages] = useState(new Set());

  const activeMeds = useSelector(selectActiveMedications);
  const archivedMeds = useSelector(selectArchivedMedications);
  const { loading } = useSelector(selectMedicationState);

  const _title = title ?? t("medicationHistoryCard.ui.title", "Medication History");
  const _subtitle = subtitle ?? t("medicationHistoryCard.ui.subtitle", "All past medications in one place.");
  const _placeholder = placeholder ?? t("medicationHistoryCard.ui.placeholder", "Search medications");

  /* =========================
   * Résolution URL image
   * ========================= */
  const resolveImageUrl = useCallback((img) => {
    if (!isValidImageValue(img)) {
      return DEFAULT_MEDICATION_IMAGE;
    }

    const value = String(img).trim();

    // Déjà une URL complète
    if (value.startsWith("http://") || value.startsWith("https://")) {
      return value;
    }

    const base = String(UPLOADS_BASE).replace(/\/+$/, "");
    const filename = value.replace(/^\/+/, "");

    return `${base}/${filename}`;
  }, []);

  /* =========================
   * Gestion erreur image
   * ========================= */
  const handleImageError = useCallback((medId) => {
    setFailedImages((prev) => new Set([...prev, medId]));
  }, []);

  /* =========================
   * Fetch medications
   * ========================= */
  useEffect(() => {
    if (patientId && !disableFetch) {
      dispatch(fetchMedications({ userId: patientId }));
      dispatch(fetchArchivedMedications({ userId: patientId }));
    }
  }, [dispatch, patientId, disableFetch]);

  /* =========================
   * Map medications
   * ========================= */
  const historyItems = useMemo(() => {
    const allMeds = [...(activeMeds || []), ...(archivedMeds || [])];

    return allMeds.map((med) => {
      const doseLabel =
        med.amount > 1
          ? t("medicationHistoryCard.ui.dosesPerDay", "doses/day")
          : t("medicationHistoryCard.ui.dosePerDay", "dose/day");

      const status = getMedicationStatus(med);

      const durationInfo = calculateDurationKey(med.startDate || med.createdAt, med.endDate);

      let durationLabel;
      if (durationInfo.key === "today" || durationInfo.key === "unknown") {
        durationLabel = t(`medicationHistoryCard.duration.${durationInfo.key}`);
      } else {
        const count = durationInfo.params.count || 1;
        durationLabel = t(`medicationHistoryCard.duration.${durationInfo.key}`, { count });
      }

      const startDateLabel =
        formatDate(med.startDate || med.createdAt, i18n.language === "ar" ? "ar-TN" : "en-GB") ||
        t("medicationHistoryCard.ui.unknownDate", "Unknown date");

      // Résolution de l'image
      const hasValidImage = isValidImageValue(med.image);
      const imageUrl = resolveImageUrl(med.image);

      return {
        id: med.id || med._id,
        name: med.name || t("medicationHistoryCard.ui.unnamed", "Unnamed medication"),
        durationLabel,
        dosage: `${med.amount} ${doseLabel}`,
        startDateLabel,
        status,
        imageUrl,
        hasValidImage,
        createdAt: med.createdAt,
        startDate: med.startDate,
        isArchived: med.isArchived,
      };
    });
  }, [activeMeds, archivedMeds, t, i18n.language, resolveImageUrl]);

  /* =========================
   * Sort items
   * ========================= */
  const sortedItems = useMemo(() => {
    return [...historyItems].sort((a, b) => {
      const dateA = new Date(a.startDate || a.createdAt || 0).getTime();
      const dateB = new Date(b.startDate || b.createdAt || 0).getTime();
      return dateB - dateA;
    });
  }, [historyItems]);

  /* =========================
   * Filter items
   * ========================= */
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sortedItems;
    return sortedItems.filter((it) => (it?.name || "").toLowerCase().includes(q));
  }, [query, sortedItems]);

  /* =========================
   * Handle search change
   * ========================= */
  const handleChange = (e) => {
    const v = e.target.value || "";
    setQuery(v);
    onSearchChange?.(v);
  };

  /* =========================
   * Render badge
   * ========================= */
  const renderBadge = (status) => {
    const s = String(status || "").toLowerCase();

    if (s === "ongoing" || s === "on going")
      return (
        <span className="mhc-badge mhc-badge-ongoing">
          {t("medicationHistoryCard.badge.ongoing", "Ongoing")}
        </span>
      );

    if (s === "completed" || s === "complited" || s === "complied")
      return (
        <span className="mhc-badge mhc-badge-completed">
          {t("medicationHistoryCard.badge.completed", "Completed")}
        </span>
      );

    if (s === "missed")
      return (
        <span className="mhc-badge mhc-badge-missed">
          {t("medicationHistoryCard.badge.missed", "Missed")}
        </span>
      );

    return <span className="mhc-badge">{status}</span>;
  };

  /* =========================
   * Get image source (fallback to default if errored)
   * ========================= */
  const getImageSrc = (item) => {
    if (failedImages.has(item.id)) {
      return DEFAULT_MEDICATION_IMAGE;
    }
    return item.imageUrl;
  };

  return (
    <div className="mhc-card">
      {/* Header */}
      <div className="mhc-head">
        <div className="mhc-titles">
          <h3 className="mhc-title">{_title}</h3>
          {_subtitle ? <div className="mhc-subtitle">{_subtitle}</div> : null}
        </div>
      </div>

      {/* Search */}
      <div className="mhc-search">
        <Input
          allowClear
          value={query}
          onChange={handleChange}
          placeholder={_placeholder}
          prefix={<SearchOutlined />}
          className="mhc-search-input"
        />
      </div>

      {/* List */}
      <div className="mhc-list">
        {loading && filtered.length === 0 ? (
          <div className="mhc-empty">{t("medicationHistoryCard.ui.loading", "Loading...")}</div>
        ) : filtered.length === 0 ? (
          <div className="mhc-empty">
            {query
              ? t("medicationHistoryCard.ui.emptySearch", "No medications found")
              : t("medicationHistoryCard.ui.empty", "No medications yet")}
          </div>
        ) : (
          filtered.map((it) => (
            <div key={it.id} className="mhc-item">
              <div className="mhc-left">
                {/* ✅ Image du médicament avec fallback SVG */}
                <div className="mhc-image-wrapper">
                  <img
                    src={getImageSrc(it)}
                    alt={it.name}
                    className="mhc-med-image"
                    onError={() => handleImageError(it.id)}
                  />
                </div>

                <div className="mhc-info">
                  <div className="mhc-name">{it.name}</div>
                  <div className="mhc-meta">
                    <span>{it.dosage}</span>
                    <span className="mhc-dot">•</span>
                    <span>{it.startDateLabel}</span>
                  </div>
                </div>
              </div>

              <div className="mhc-right">
                <div className="mhc-duration">{it.durationLabel}</div>
                {renderBadge(it.status)}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}